import React, { useState } from 'react';
import { completePriceListFormat } from '../../api';
import { COMPLETION_FIELD_LABELS, FormatCompletionNotice } from './FormatCompletionNotice';
import VarietyEditorDialog from '../varietyPricing/VarietyEditorDialog';
import { isRepeatableProductFormat } from '../../utils/varietyFormatIdentity';
import { SHARED_RETAIL_FIELDS } from '../../utils/sharedRetailConfiguration';

const optionalNumber = (value) => String(value).trim() === '' ? null : Number(value);
const identityValue = (field, value) => {
  if (field === 'useScaleWeight') return Boolean(value);
  if (['unitWeightKg', 'baseQuantityPerUnit'].includes(field)) return optionalNumber(value ?? '');
  return String(value ?? '').trim() || null;
};

export default function FormatCompletionDialog({ branchId, product, variety, format, canUpdateProduct, canUpdatePrice, onSaved, onClose, saveFormat, registration = false, lockIdentity = false }) {
  const [form, setForm] = useState(() => ({
    barcode: format.barcode ?? '', metricUnit: format.metricUnit ?? '',
    unitWeightKg: format.unitWeightKg ?? '', baseQuantityPerUnit: format.baseQuantityPerUnit ?? '',
    useScaleWeight: Boolean(format.useScaleWeight), sellingPrice: format.sellingPrice ?? '',
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [policyReview, setPolicyReview] = useState(null);
  const priceEditable = canUpdatePrice && !format.isPriceLocked;
  const barcodeOptional = registration || isRepeatableProductFormat(format);
  const showWeight = registration || Boolean(form.unitWeightKg)
    || (!form.useScaleWeight && (product.pricingBasis === 'kg' || variety.inventoryBaseUnit === 'kg'));
  const showInventoryConversion = product.category?.tracksInventory !== false || Boolean(form.baseQuantityPerUnit);
  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => ({ ...current, [field]: '' }));
    setPolicyReview(null);
    setError('');
  };
  const save = async (acknowledgePolicyWarnings = false) => {
    if (busy) return;
    const identity = {};
    const errors = {};
    if (canUpdateProduct) {
      for (const field of ['barcode', 'metricUnit', 'unitWeightKg', 'baseQuantityPerUnit', 'useScaleWeight']) {
        if (format.sharesRetailConfiguration && SHARED_RETAIL_FIELDS.includes(field)) continue;
        const value = identityValue(field, form[field]);
        if ((registration && !format.id) || value !== identityValue(field, format[field])) identity[field] = value;
      }
      if (identity.barcode && !/^\d{1,7}$/.test(identity.barcode)) errors.barcode = 'Usa entre 1 y 7 dígitos.';
      for (const field of ['unitWeightKg', 'baseQuantityPerUnit']) {
        if (Object.hasOwn(identity, field) && identity[field] !== null && (!Number.isFinite(identity[field]) || identity[field] <= 0)) errors[field] = 'Debe ser un número mayor a 0.';
      }
    }
    const branch = {};
    if (priceEditable && String(form.sellingPrice) !== String(format.sellingPrice ?? '')) {
      if (!String(form.sellingPrice).trim() || !Number.isSafeInteger(Number(form.sellingPrice)) || Number(form.sellingPrice) < 0) errors.sellingPrice = 'Ingresa un precio entero mayor o igual a 0.';
      else branch.sellingPrice = Number(form.sellingPrice);
    }
    setFieldErrors(errors);
    const invalid = Object.keys(errors)[0];
    if (invalid) { document.getElementById(`complete-format-${invalid}`)?.focus(); return; }
    if (!registration && !Object.keys(identity).length && !Object.keys(branch).length) { onClose(); return; }
    setBusy(true);
    setError('');
    try {
      const payload = { identity, branch, acknowledgePolicyWarnings };
      const response = saveFormat ? await saveFormat(payload) : await completePriceListFormat(branchId, format.id, payload);
      await onSaved(response);
      onClose();
    } catch (requestError) {
      const data = requestError?.response?.data;
      if (data?.code === 'pricing_policy_confirmation_required') setPolicyReview(data);
      else {
        setError(data?.message || 'No se pudieron guardar los datos. Puedes reintentar.');
        if (data?.field) setFieldErrors({ [data.field]: data.message });
      }
    } finally { setBusy(false); }
  };
  const visibleMissing = (format.completion?.missingFields || []).filter((field) => !format.sharesRetailConfiguration || !SHARED_RETAIL_FIELDS.includes(field));
  const visibleCompletion = format.completion ? { ...format.completion, missingFields: visibleMissing,
    isReady: visibleMissing.length === 0, isDraft: format.completion.isDraft && visibleMissing.length > 0 } : null;
  const input = (field, editable, options = {}) => <label className="block text-sm font-medium text-slate-800" htmlFor={`complete-format-${field}`}>
    {COMPLETION_FIELD_LABELS[field]}{field === 'barcode' && barcodeOptional ? ' (opcional)' : ''}
    <input id={`complete-format-${field}`} value={form[field]} onChange={(event) => update(field, event.target.value)} disabled={busy || !editable}
      aria-invalid={Boolean(fieldErrors[field])} aria-describedby={fieldErrors[field] ? `complete-format-${field}-error` : undefined}
      className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 disabled:bg-slate-100" {...options} />
    {fieldErrors[field] && <span id={`complete-format-${field}-error`} role="alert" className="text-xs text-red-700">{fieldErrors[field]}</span>}
  </label>;

  return <VarietyEditorDialog eyebrow={registration ? 'Registrar formato' : 'Completar formato'} title={`${product.name} · ${variety.name} · ${format.productFormatName || format.clientTypeLabel || 'Pack'}`}
    description="Puedes guardar avances y completar los campos pendientes después. Un borrador estará disponible cuando tenga sus datos de venta completos."
    busy={busy} onClose={onClose} closeLabel={registration ? 'Cerrar registrar formato' : 'Cerrar completar datos'}
    footerNote="Los costos, reglas de precio, imágenes y descripciones son opcionales."
    footerActions={<><button type="button" disabled={busy} onClick={onClose} className="rounded-lg border px-4 py-2">Cancelar</button>
      <button type="button" disabled={busy || (!canUpdateProduct && !priceEditable)} onClick={() => save(false)} className="rounded-lg bg-blue-600 px-4 py-2 text-white disabled:opacity-50">{busy ? 'Guardando...' : 'Guardar datos'}</button></>}>
    <div className="mx-auto max-w-3xl space-y-5">
      <FormatCompletionNotice completion={visibleCompletion} />
      <fieldset className="rounded-xl border p-4"><legend className="px-2 font-semibold">Datos del formato · Todas las sucursales</legend>
        {format.sharesRetailConfiguration && <p className="mb-3 text-sm text-slate-600">La unidad, el peso, la balanza y la equivalencia se configuran en Detalle y se comparten con Oferta.</p>}
        {!canUpdateProduct && <p className="mb-3 text-xs text-slate-600">Necesitas permiso para editar productos para modificar estos campos.</p>}
        <div className="grid gap-4 sm:grid-cols-2">
          {input('barcode', canUpdateProduct, { inputMode: 'numeric', maxLength: 7 })}
          {input('metricUnit', canUpdateProduct && !lockIdentity && !format.sharesRetailConfiguration, { placeholder: 'Ej. kg, unidad, caja' })}
          <label className="flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" checked={form.useScaleWeight} onChange={(event) => update('useScaleWeight', event.target.checked)} disabled={busy || !canUpdateProduct || format.sharesRetailConfiguration} />Vender por peso medido en balanza</label>
          {showWeight && input('unitWeightKg', canUpdateProduct && !lockIdentity && !format.sharesRetailConfiguration, { inputMode: 'decimal' })}
          {showInventoryConversion && input('baseQuantityPerUnit', canUpdateProduct && !format.sharesRetailConfiguration, { inputMode: 'decimal' })}
        </div>
        {showInventoryConversion && <p className="mt-2 text-xs text-slate-500">Equivalencia: cantidad de {variety.inventoryBaseUnit || 'la unidad base'} de inventario por unidad vendida. Para ventas directas en la unidad base no hace falta indicarla.</p>}
      </fieldset>
      <fieldset className="rounded-xl border p-4"><legend className="px-2 font-semibold">Precio de esta sucursal</legend>
        {format.isPriceLocked && <p className="mb-2 text-xs text-amber-800">El precio está bloqueado. Desbloquéalo para editarlo.</p>}
        {!canUpdatePrice && <p className="mb-2 text-xs text-slate-600">Necesitas permiso para editar precios.</p>}
        {input('sellingPrice', priceEditable, { inputMode: 'numeric' })}
      </fieldset>
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {policyReview && <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
        <p>{policyReview.message}</p>
        <ul className="my-2 list-disc pl-5">{(policyReview.warnings || []).map((warning, index) => <li key={index}>{warning.message || warning.code || warning.type}</li>)}</ul>
        <button type="button" disabled={busy} onClick={() => save(true)} className="font-semibold underline">Confirmar precio y guardar</button>
      </div>}
    </div>
  </VarietyEditorDialog>;
}
